import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSaccosStore } from '@/stores/saccos'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/LoginPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/',
      component: () => import('@/components/layout/MainLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/pages/DashboardPage.vue'),
        },
        {
          path: 'saccos',
          name: 'saccos',
          component: () => import('@/pages/saccos/SaccosListPage.vue'),
        },
        {
          path: 'saccos/new',
          name: 'sacco-create',
          component: () => import('@/pages/saccos/SaccoCreatePage.vue'),
        },
        {
          path: 'saccos/:id',
          name: 'sacco-layout',
          component: () => import('@/pages/saccos/SaccoLayoutPage.vue'),
          redirect: { name: 'sacco-overview' },
          children: [
            {
              path: 'overview',
              name: 'sacco-overview',
              component: () => import('@/pages/saccos/SaccoOverviewPage.vue'),
            },
            {
              path: 'settings',
              name: 'sacco-settings',
              component: () => import('@/pages/saccos/SaccoSettingsPage.vue'),
            },
            {
              path: 'reconciliations',
              name: 'sacco-reconciliations',
              component: () => import('@/pages/saccos/SaccoReconciliationsPage.vue'),
            },
            // Legacy redirects — Branding/Hostnames/M-Pesa now live as tabs inside settings.
            { path: 'branding', redirect: (to) => ({ name: 'sacco-settings', params: to.params, query: { tab: 'branding' } }) },
            { path: 'hostnames', redirect: (to) => ({ name: 'sacco-settings', params: to.params, query: { tab: 'hostnames' } }) },
            { path: 'mpesa', redirect: (to) => ({ name: 'sacco-settings', params: to.params, query: { tab: 'mpesa' } }) },
            {
              path: 'disbursements',
              name: 'sacco-disbursements',
              component: () => import('@/pages/saccos/SaccoDisbursementsPage.vue'),
              beforeEnter: async (to, _from, next) => {
                const saccosStore = useSaccosStore()
                const id = to.params.id as string
                if (!saccosStore.currentSacco || saccosStore.currentSacco.id !== id) {
                  await saccosStore.fetchSacco(id)
                }
                if (saccosStore.currentSacco?.payment_model !== 'veripay') {
                  next({ name: 'sacco-overview', params: { id } })
                } else {
                  next()
                }
              },
            },
            // Legacy redirect
            { path: 'pending-withdrawals', redirect: (to) => ({ name: 'sacco-disbursements', params: to.params }) },
            {
              path: 'users',
              name: 'sacco-users',
              component: () => import('@/pages/saccos/SaccoUsersPage.vue'),
            },
            {
              path: 'users/new',
              name: 'sacco-user-create',
              component: () => import('@/pages/users/UserCreatePage.vue'),
            },
            {
              path: 'revenue',
              name: 'sacco-revenue',
              component: () => import('@/pages/saccos/SaccoRevenuePage.vue'),
              beforeEnter: async (to, _from, next) => {
                const saccosStore = useSaccosStore()
                const id = to.params.id as string
                if (!saccosStore.currentSacco || saccosStore.currentSacco.id !== id) {
                  await saccosStore.fetchSacco(id)
                }
                if (saccosStore.currentSacco?.payment_model !== 'veripay') {
                  next({ name: 'sacco-overview', params: { id } })
                } else {
                  next()
                }
              },
            },
            {
              path: 'central-wallet',
              name: 'sacco-central-wallet',
              component: () => import('@/pages/saccos/SaccoCentralWalletPage.vue'),
              beforeEnter: async (to, _from, next) => {
                const saccosStore = useSaccosStore()
                const id = to.params.id as string
                if (!saccosStore.currentSacco || saccosStore.currentSacco.id !== id) {
                  await saccosStore.fetchSacco(id)
                }
                if (saccosStore.currentSacco?.payment_model !== 'veripay') {
                  next({ name: 'sacco-overview', params: { id } })
                } else {
                  next()
                }
              },
            },
            {
              path: 'platform-wallet',
              name: 'sacco-platform-wallet',
              component: () => import('@/pages/saccos/SaccoPlatformWalletPage.vue'),
              beforeEnter: async (to, _from, next) => {
                const saccosStore = useSaccosStore()
                const id = to.params.id as string
                if (!saccosStore.currentSacco || saccosStore.currentSacco.id !== id) {
                  await saccosStore.fetchSacco(id)
                }
                if (saccosStore.currentSacco?.payment_model !== 'veripay') {
                  next({ name: 'sacco-overview', params: { id } })
                } else {
                  next()
                }
              },
            },
            {
              path: 'passenger-transfers',
              name: 'sacco-passenger-transfers',
              component: () => import('@/pages/saccos/SaccoPassengerTransfersPage.vue'),
              beforeEnter: async (to, _from, next) => {
                const saccosStore = useSaccosStore()
                const id = to.params.id as string
                if (!saccosStore.currentSacco || saccosStore.currentSacco.id !== id) {
                  await saccosStore.fetchSacco(id)
                }
                if (saccosStore.currentSacco?.payment_model !== 'veripay') {
                  next({ name: 'sacco-overview', params: { id } })
                } else {
                  next()
                }
              },
            },
            // Legacy redirect — Bank Accounts now lives as a tab inside Settings.
            {
              path: 'bank-accounts',
              redirect: (to) => ({
                name: 'sacco-settings',
                params: to.params,
                query: { tab: 'bank-accounts' },
              }),
            },
            {
              path: 'exposure',
              name: 'sacco-exposure',
              component: () => import('@/pages/saccos/SaccoExposurePage.vue'),
              beforeEnter: async (to, _from, next) => {
                const saccosStore = useSaccosStore()
                const id = to.params.id as string
                if (!saccosStore.currentSacco || saccosStore.currentSacco.id !== id) {
                  await saccosStore.fetchSacco(id)
                }
                if (saccosStore.currentSacco?.payment_model !== 'veripay') {
                  next({ name: 'sacco-overview', params: { id } })
                } else {
                  next()
                }
              },
            },
            {
              path: 'exposure/audit/vehicle/:vehicleId',
              name: 'sacco-exposure-vehicle-audit',
              component: () => import('@/pages/saccos/SaccoExposureVehicleAuditPage.vue'),
              beforeEnter: async (to, _from, next) => {
                const saccosStore = useSaccosStore()
                const id = to.params.id as string
                if (!saccosStore.currentSacco || saccosStore.currentSacco.id !== id) {
                  await saccosStore.fetchSacco(id)
                }
                if (saccosStore.currentSacco?.payment_model !== 'veripay') {
                  next({ name: 'sacco-overview', params: { id } })
                } else {
                  next()
                }
              },
            },
            {
              path: 'exposure/audit/user/:userId',
              name: 'sacco-exposure-user-audit',
              component: () => import('@/pages/saccos/SaccoExposureUserAuditPage.vue'),
              beforeEnter: async (to, _from, next) => {
                const saccosStore = useSaccosStore()
                const id = to.params.id as string
                if (!saccosStore.currentSacco || saccosStore.currentSacco.id !== id) {
                  await saccosStore.fetchSacco(id)
                }
                if (saccosStore.currentSacco?.payment_model !== 'veripay') {
                  next({ name: 'sacco-overview', params: { id } })
                } else {
                  next()
                }
              },
            },
            {
              path: 'users/:userId',
              name: 'sacco-user-detail',
              component: () => import('@/pages/saccos/SaccoUserDetailPage.vue'),
            },
            {
              path: 'danger',
              name: 'sacco-danger',
              component: () => import('@/pages/saccos/SaccoDangerZonePage.vue'),
            },
          ],
        },
        {
          path: 'users',
          name: 'users',
          component: () => import('@/pages/users/UsersListPage.vue'),
        },
        {
          // Top-level create — the only route that can reach the platform
          // band (PLATFORM_OPS). Must be declared before `users/:id` so
          // "new" isn't swallowed as an id.
          path: 'users/new',
          name: 'user-create',
          component: () => import('@/pages/users/UserCreatePage.vue'),
          meta: { permission: 'admin.user.manage' },
        },
        {
          path: 'users/:id',
          name: 'user-detail',
          component: () => import('@/pages/users/UserDetailPage.vue'),
        },
        {
          path: 'banks',
          name: 'banks',
          component: () => import('@/pages/BanksPage.vue'),
        },
        {
          // The stage catalog is global and shared by Ma3Club routes and VeriPay
          // F1 stage fees, so it sits at the top level rather than under Ma3Club.
          path: 'stages',
          name: 'stages',
          component: () => import('@/pages/StagesPage.vue'),
          meta: { superAdminOnly: true },
        },
        {
          path: 'ma3club',
          name: 'ma3club-layout',
          meta: { superAdminOnly: true },
          component: () => import('@/pages/ma3club/Ma3ClubLayoutPage.vue'),
          redirect: { name: 'ma3club-overview' },
          children: [
            { path: 'overview', name: 'ma3club-overview', component: () => import('@/pages/ma3club/Ma3ClubOverviewPage.vue') },
            { path: 'bookings', name: 'ma3club-bookings', component: () => import('@/pages/ma3club/Ma3ClubBookingsPage.vue') },
            { path: 'passengers', name: 'ma3club-passengers', component: () => import('@/pages/ma3club/Ma3ClubPassengersPage.vue') },
            { path: 'passengers/:id', name: 'ma3club-passenger-detail', component: () => import('@/pages/ma3club/Ma3ClubPassengerDetailPage.vue') },
            { path: 'routes', name: 'ma3club-routes', component: () => import('@/pages/ma3club/Ma3ClubRoutesPage.vue') },
            { path: 'routes/:id', name: 'ma3club-route-detail', component: () => import('@/pages/ma3club/Ma3ClubRouteDetailPage.vue') },
            // Moved to the top level; kept so old links don't 404.
            { path: 'stages', redirect: { name: 'stages' } },
            { path: 'operators', name: 'ma3club-operators', component: () => import('@/pages/ma3club/Ma3ClubOperatorsPage.vue') },
            { path: 'settlements', name: 'ma3club-settlements', component: () => import('@/pages/ma3club/Ma3ClubSettlementsPage.vue') },
            { path: 'settings', name: 'ma3club-settings', component: () => import('@/pages/ma3club/Ma3ClubSettingsPage.vue') },
          ],
        },
        {
          path: 'revenue',
          name: 'revenue',
          component: () => import('@/pages/RevenuePage.vue'),
          meta: { permission: 'admin.revenue.view' },
        },
        {
          // The cross-sacco backlog. Per-sacco exposure still lives under the
          // sacco rail; this is the same registry with the sacco column on.
          path: 'exposure',
          name: 'exposure',
          component: () => import('@/pages/ExposurePage.vue'),
          meta: { permission: 'exposure.audit' },
        },
        // Legacy redirect — sacco revenue now lives under the sacco rail
        {
          path: 'revenue/sacco/:id',
          redirect: (to) => ({ name: 'sacco-revenue', params: { id: to.params.id } }),
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('@/pages/SettingsPage.vue'),
        },
        {
          path: 'maintenance',
          name: 'maintenance',
          component: () => import('@/pages/MaintenancePage.vue'),
          meta: { superAdminOnly: true },
        },
        {
          // PLATFORM templates are SUPER_ADMIN-only server-side (a delegated
          // granter gets a 403 on save), so the page is too.
          path: 'permission-templates',
          name: 'permission-templates',
          component: () => import('@/pages/permissions/PermissionTemplatesPage.vue'),
          meta: { superAdminOnly: true },
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
})

/**
 * Can the signed-in caller reach this route?
 *
 * Routes declare what they need (`meta.permission`, or `meta.superAdminOnly`
 * for the pages whose endpoints are a strict SUPER_ADMIN gate). A caller who
 * lacks it is sent to the dashboard rather than shown a page of red error
 * boxes — an ops user holding only `admin.access` used to see Revenue and
 * Permissions in the rail and get exactly that (audit A-B4).
 */
function mayEnter(to: { meta: Record<string, unknown> }): boolean {
  const authStore = useAuthStore()
  const isSuper = authStore.user?.role === 'SUPER_ADMIN'
  if (isSuper) return true
  if (to.meta.superAdminOnly) return false
  const needed = to.meta.permission as string | undefined
  if (!needed) return true
  const perms = authStore.user?.permissions || []
  return perms.includes('*') || perms.includes(needed)
}

// Navigation guard
router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore()

  if (to.meta.requiresAuth !== false && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } })
  } else if (to.name === 'login' && authStore.isAuthenticated) {
    next({ name: 'dashboard' })
  } else if (to.meta.requiresAuth !== false && authStore.isAuthenticated && !authStore.canAccessAdminPortal) {
    // SUPER_ADMIN bypasses; PLATFORM_OPS needs the admin.access perm grant.
    authStore.logout()
    next({ name: 'login', query: { error: 'unauthorized' } })
  } else if (to.meta.requiresAuth !== false && authStore.isAuthenticated && !mayEnter(to)) {
    next({ name: 'dashboard' })
  } else {
    next()
  }
})

export default router
